import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';

import { findBotCommandContext, insertBotCommand, insertValueFor } from '../utils/bot_commands';

import { AutosuggestBotCommand } from './autosuggest_bot_command';
import botCommandClasses from './autosuggest_bot_command.module.scss';

const suggestionsKey = (context) =>
  context ? context.suggestions.map((s) => `${s.acct ?? ''}:${s.kind}:${s.label}`).join('|') : '';

/**
 * Suggestions for bot commands in square brackets (see utils/bot_commands),
 * for AutosuggestTextarea. They are worked out from the commands of the
 * mentioned bots, without asking the server. Kept apart from the textarea,
 * which is Mastodon's own, so that it only needs a few lines to use them.
 */
export const useBotCommandSuggestions = ({ botCommands, onBotCommandInsert, value, textareaRef }) => {
  const [context, setContext] = useState(null);
  const [selected, setSelected] = useState(0);
  const [hidden, setHidden] = useState(false);
  const visible = !!context && !hidden;

  const lastCaretRef = useRef(null);
  const lastKeyRef = useRef('');

  const update = useCallback((text, caret) => {
    const next = botCommands?.length > 0 ? findBotCommandContext(text, caret, botCommands) : null;
    const key = suggestionsKey(next);

    lastCaretRef.current = `${caret}:${text}`;
    setContext(next);

    // Keep the highlighted suggestion when the list itself did not change
    if (key !== lastKeyRef.current) {
      lastKeyRef.current = key;
      setSelected(0);
    }
  }, [botCommands]);

  const insert = useCallback((index) => {
    const suggestion = context?.suggestions[index];
    if (!suggestion) return;

    // Parts written freely put in something to start from (see
    // insertValueFor); one with nothing to put in only explains itself
    if (!onBotCommandInsert || insertValueFor(suggestion.node) === '') {
      setHidden(true);
      return;
    }

    const { text, caret } = insertBotCommand(value ?? '', context, suggestion);
    onBotCommandInsert(text, caret);
    update(text, caret);
  }, [context, onBotCommandInsert, value, update]);

  /** To call when the text changes */
  const onTextChange = useCallback((text, caret) => {
    setHidden(false);
    update(text, caret);
  }, [update]);

  /** The caret moved (click, arrow keys): show what fits at the new place */
  const handleSelect = useCallback((e) => {
    const { selectionStart, selectionEnd, value: text } = e.target;

    if (selectionStart === selectionEnd && lastCaretRef.current !== `${selectionStart}:${text}`) {
      update(text, selectionStart);
    }
  }, [update]);

  /** Returns true when the key was used for the suggestions */
  const handleKeyDown = useCallback((e) => {
    if (!visible || !['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) return false;

    // Ctrl/Cmd+Enter still posts
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) return false;

    e.preventDefault();
    e.stopPropagation();

    const count = context.suggestions.length;

    if (e.key === 'ArrowDown') {
      setSelected((selected + 1) % count);
    } else if (e.key === 'ArrowUp') {
      setSelected((selected - 1 + count) % count);
    } else if (e.key === 'Escape') {
      setHidden(true);
    } else {
      insert(selected);
    }

    return true;
  }, [visible, context, selected, insert]);

  const hide = useCallback(() => {
    setHidden(true);
  }, []);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    insert(Number(e.currentTarget.getAttribute('data-index')));
    textareaRef.current?.focus();
  }, [insert, textareaRef]);

  const renderSuggestions = useCallback(() => context.suggestions.map((suggestion, i) => (
    <div
      role='button'
      tabIndex={0}
      key={`${suggestion.acct ?? ''}:${suggestion.kind}:${suggestion.label}`}
      data-index={i}
      className={classNames('autosuggest-textarea__suggestions__item', botCommandClasses.item, { selected: i === selected })}
      onMouseDown={handleClick}
    >
      <AutosuggestBotCommand suggestion={suggestion} />
    </div>
  )), [context, selected, handleClick]);

  // The commands of a bot that was just mentioned arrive a moment later
  useEffect(() => {
    const element = textareaRef.current;

    if (element && element === document.activeElement) {
      update(element.value, element.selectionStart);
    }
  }, [botCommands, textareaRef, update]);

  return useMemo(
    () => ({ visible, onTextChange, handleSelect, handleKeyDown, hide, renderSuggestions }),
    [visible, onTextChange, handleSelect, handleKeyDown, hide, renderSuggestions],
  );
};
